import React, {memo, useEffect} from 'react'
import _ from 'lodash'
import {cache} from 'swr'
import {Accordion, Button, Header, Icon, Label, List, Message, Modal} from 'semantic-ui-react'
import {withConflictsData, withErrorHandling, withPropsPresent, withVersionData} from '../../hoc'
import {removeResource, removeResponse, syncContract} from '../../backend'
import {useSafeState} from '../../hook'

const _ContractConflicts = (
  {
    conflicts = [],
    serviceId,
    versionId,
    syncContract,
    navigate,
    removeResource,
    removeResponse,
    mutateVersion
  }
) => {
  const [state, setState] = useSafeState({
    syncing: false,
    viewing: false,
    removingResource: '',
    removingResponse: '',
    activeEnvironmentConflictIndex: -1,
    activeResourceConflictIndex: -1,
    activeResponseConflictIndex: -1,
    conflicts: []
  })

  useEffect(() => {
    setState({...state, conflicts})
  }, [conflicts])

  useEffect(() => {
    setState({...state, conflicts})
  }, [serviceId, versionId, conflicts])

  const navToVersionIfNoConflictsPresent = (conflicts = []) => {
    if (!conflicts.length) {
      navigate(`/c/services/${serviceId}/versions/${versionId}/environments`)
    }
  }

  const onSync = async () => {
    setState({...state, syncing: true})
    await syncContract(serviceId, versionId)
    setState({...state, syncing: false, viewing: false, conflicts: []})
    cache.clear()
    mutateVersion()
    navToVersionIfNoConflictsPresent(conflicts)
  }

  const onRemoveResource = async (e, resourceId) => {
    e.preventDefault()
    e.stopPropagation()
    setState({...state, removingResource: resourceId})
    await removeResource(resourceId)
    const conflicts = state.conflicts.filter(c => {
      return c.resource.id !== resourceId
    })
    mutateVersion()
    setState({...state, removingResource: '', conflicts})
    navToVersionIfNoConflictsPresent(conflicts)
  }

  const onRemoveResponse = async (e, responseId) => {
    setState({...state, removingResponse: responseId})
    await removeResponse(responseId)
    const conflicts = state.conflicts.filter(c => {
      return c.response.id !== responseId
    })
    mutateVersion()
    setState({...state, removingResponse: '', conflicts})
    navToVersionIfNoConflictsPresent(conflicts)
  }

  const onView = (e) => {
    e.preventDefault()
    setState({...state, viewing: true})
  }

  const onHide = async () => {
    setState({...state, viewing: false})
  }

  const onSelectEnvironmentConflict = (e, titleProps) => {
    const {index} = titleProps
    const {activeEnvironmentConflictIndex} = state
    const newIndex = activeEnvironmentConflictIndex === index ? -1 : index
    setState({
      ...state,
      activeEnvironmentConflictIndex: newIndex,
      activeResourceConflictIndex: -1,
      activeResponseConflictIndex: -1
    })
  }

  const onSelectResourceConflict = (e, titleProps) => {
    const {index} = titleProps
    const {activeResourceConflictIndex} = state
    const newIndex = activeResourceConflictIndex === index ? -1 : index
    setState({...state, activeResourceConflictIndex: newIndex, activeResponseConflictIndex: -1})
  }

  const onSelectResponseConflict = (e, titleProps) => {
    const {index} = titleProps
    const {activeResponseConflictIndex} = state
    const newIndex = activeResponseConflictIndex === index ? -1 : index
    setState({...state, activeResponseConflictIndex: newIndex})
  }

  const getColorByHttpMethod = (method) => {
    switch (method) {
      case 'GET':
        return 'blue'
      case 'POST':
        return 'green'
      case 'PUT':
        return 'yellow'
      case 'PATCH':
        return 'orange'
      case 'DELETE':
        return 'red'
      case 'HEAD':
        return 'grey'
      case 'OPTIONS':
        return 'olive'
    }
  }

  const getEnvironments = (conflicts) => {
    return _.reduce(conflicts, (environments, conflict) => {
      if (!environments.some(e => e.id === conflict.environment.id)) {
        environments.push(conflict.environment)
      }
      return environments
    }, [])
  }

  const getResources = (environment, conflicts) => {
    return conflicts.filter(c => c.environment.name === environment.name).reduce((resources, conflict) => {
      let resource = resources.find(r => r.id === conflict.resource.id)
      if (!resource) {
        resource = conflict.resource
        if (conflict.source !== 'resource') {
          delete resource.message
        }
        resources.push(resource)
      }
      return resources
    }, [])
  }

  const getEnvironmentConflictCount = (environment, conflicts) => {
    return conflicts.filter(c => c.environment.id === environment.id).length
  }

  const getResourceConflictCount = (resourceId, conflicts) => {
    return conflicts.filter(c => c.source === 'response' && c.resource.id === resourceId).length
  }


  const renderResourceConflict = (environment, resource, conflicts) => {
    const resourceConflict = conflicts.find(c => c.source === 'resource' && c.environment.id === environment.id && c.resource.path === resource.path && c.resource.verb === resource.verb)
    if (resourceConflict) {
      return (
        <>
          {
            !!resourceConflict.message && (
              <>
                <p>{resourceConflict.message}</p>
                <Button title='Remove Resource' circular compact size='mini' basic icon='remove' primary
                        content='Remove Resource From Mock'
                        data-test='conflict-remove-resource-button'
                        loading={state.removingResource === resourceConflict.resource.id}
                        onClick={async (e) => {
                          return onRemoveResource(e, resource.id)
                        }}/>
              </>
            )
          }
          {renderResourceResponseConflicts(environment, resource, conflicts)}
        </>
      )
    }
    return renderResourceResponseConflicts(environment, resource, conflicts)
  }

  const renderResourceConflicts = (environment, conflicts) => {
    return (
      <>
        <Header as='h4' dividing>Resource Conflicts</Header>
        <Accordion styled fluid data-test='conflict-resources'>
          {
            getResources(environment, conflicts).map((resource, i) => {
              const childCount = getResourceConflictCount(resource.id, conflicts)
              return (
                <React.Fragment key={`resource_${i}`}>
                  <Accordion.Title key={`resource_title_${i}`} index={i}
                                   active={state.activeResourceConflictIndex === i}
                                   onClick={onSelectResourceConflict}>
                    <Icon name='dropdown'/>
                    <List horizontal style={{display: 'inline'}}>
                      <List.Item>
                        <Label color={getColorByHttpMethod(resource.verb)}
                               data-test='conflict-resource-verb'
                               style={{
                                 marginRight: '10px',
                                 width: '75px',
                                 textAlign: 'center'
                               }}>{resource.verb.toUpperCase()}</Label>
                      </List.Item>
                      <List.Item style={{maxWidth: '80%'}} data-test='conflict-resource-path'>
                        <b style={{whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{resource.path}</b>
                      </List.Item>
                      {
                        childCount > 0 && (
                          <List.Item style={{float: 'right'}}>
                            <Label circular color='olive'>
                              {childCount}
                            </Label>
                          </List.Item>
                        )
                      }
                    </List>
                  </Accordion.Title>
                  <Accordion.Content key={`resource_content_${i}`} active={state.activeResourceConflictIndex === i}>
                    {renderResourceConflict(environment, resource, conflicts)}
                  </Accordion.Content>
                </React.Fragment>
              )
            })
          }
        </Accordion>
      </>
    )
  }

  const renderResponseConflictSchemaErrors = (responseConflict) => {
    if (!responseConflict.schemaErrors || !responseConflict.schemaErrors.length) {
      return null
    }
    return (
      <>
        <Header as='h4' dividing>Schema Error(s)</Header>
        <Message>
        <pre className='pre-wrap' data-test='conflict-response-schema-errors'>
        {
          JSON.stringify(responseConflict.schemaErrors, null, 2).replace(/\\n/g, '')
        }
        </pre>
        </Message>
      </>
    )
  }

  const renderResponseConflict = (environment, resource, responseConflict) => {
    const orphaned = responseConflict.code === 'RESPONSE_ORPHANED'
    return (
      <>
        <p>
          {responseConflict.message}
        </p>
        <Button title='View' circular compact size='mini' basic icon='folder open outline'
                color={orphaned ? 'red' : 'grey'}
                content={orphaned ? 'Remove Response From Mock' : 'Edit Response In Mock'}
                loading={state.removingResponse === responseConflict.response.id}
                data-test='conflict-remove-response-button'
                onClick={async (e) => {
                  if (orphaned) {
                    return onRemoveResponse(e, responseConflict.response.id)
                  } else {
                    navigate(responseConflict.consolePath)
                  }
                }}/>
        <br/>
        {renderResponseConflictSchemaErrors(responseConflict)}
      </>
    )
  }

  const renderResourceResponseConflicts = (environment, resource, conflicts) => {
    const resourceResponseConflicts = conflicts.filter(c => c.source === 'response' && c.environment.id === environment.id && c.resource.path === resource.path && c.resource.verb === resource.verb)
    if (resourceResponseConflicts.length) {
      return (
        <div>
          <Header as='h4' dividing>Response Conflicts</Header>
          <Accordion styled fluid data-test='conflict-responses'>
            {
              resourceResponseConflicts.map((resourceResponseConflict, i) => {
                return (
                  <React.Fragment key={`response${i}`}>
                    <Accordion.Title key={`response_title_${i}`} index={i}
                                     active={state.activeResponseConflictIndex === i}
                                     onClick={onSelectResponseConflict}>
                      <Icon name='dropdown'/>
                      <List horizontal style={{display: 'inline'}}>
                        <List.Item data-test='conflict-response-details'>
                          Status={resourceResponseConflict.response.status} /
                          Name={resourceResponseConflict.response.name} /
                          Content-Type={resourceResponseConflict.response.contentType}
                        </List.Item>
                      </List>
                    </Accordion.Title>
                    <Accordion.Content key={`response_content_${i}`} active={state.activeResponseConflictIndex === i}>
                      {renderResponseConflict(environment, resource, resourceResponseConflict)}
                    </Accordion.Content>
                  </React.Fragment>
                )
              })
            }
          </Accordion>
        </div>
      )
    }
    return null
  }

  if (!state.conflicts.length) {
    return null
  }

  return (
    <>
      <Message>
        <Message.Header>Differences found between mock and contract</Message.Header>
        <p>
          {/*To reconcile these differences, you can click sync and the mock will match*/}
          To reconcile these differences, you can review and assess each of them and modify the mock and/or contract
          until you've
          removed all conflicts.
        </p>
        <div style={{marginTop: '15px'}}>
          <Button content='View Conflicts' size='tiny' icon='list alternate outline' onClick={onView} data-testid='conflict-view-button'/>
        </div>
      </Message>
      <Modal open={state.viewing} closeOnDimmerClick={true} closeIcon={true} onClose={onHide} size='large'
             data-testid='conflicts-modal'>
        <Header content='Mock/Contract Conflicts'/>
        <Modal.Content scrolling>
          <Message header='Warning - Before You Choose To Sync'
                   list={[
                     'Consider that mock resources and/or responses not presently defined in contract yaml/json will be removed',
                     'Review your contract to ensure you do not remove unintended mock resources and/or responses',
                     'Remember that conflicts stemming from contract validation issues need to be addressed by reconciling your responses w/ their contract definition'
                   ]}/>

          <Header as='h4' dividing>Environment Conflicts</Header>
          <Accordion styled fluid data-testid='conflict-environments'>
            {
              getEnvironments(state.conflicts).map((environment, i) => {
                const childCount = getEnvironmentConflictCount(environment, state.conflicts)
                return (
                  <React.Fragment key={`environment_${environment.id}`}>
                    <Accordion.Title key={`environment_title_${i}`} index={i}
                                     active={state.activeResourceConflictIndex === i}
                                     onClick={onSelectEnvironmentConflict}>
                      <Icon name='dropdown'/>
                      <List horizontal style={{display: 'inline'}}>
                        <List.Item data-test='conflict-environment-name'>
                          {environment.name}
                        </List.Item>
                        {
                          childCount > 0 && (
                            <List.Item style={{float: 'right'}}>
                              <Label circular color='olive'>
                                {childCount}
                              </Label>
                            </List.Item>
                          )
                        }
                      </List>
                    </Accordion.Title>
                    <Accordion.Content key={`environment_content_${i}`}
                                       active={state.activeEnvironmentConflictIndex === i}>
                      {renderResourceConflicts(environment, state.conflicts)}
                    </Accordion.Content>
                  </React.Fragment>
                )
              })
            }
          </Accordion>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onHide} data-testid='conflicts-close-button'>Close</Button>
          <Button color='olive' onClick={onSync} loading={state.syncing} icon='sync' content='Sync'
                  data-testid='conflicts-sync-button'/>
        </Modal.Actions>
      </Modal>
    </>
  )
}

let ContractConflicts = (props) => {
  const {handleError} = props
  return (<_ContractConflicts {...{
    ...props,
    syncContract: handleError(syncContract),
    removeResource: handleError(removeResource),
    removeResponse: handleError(removeResponse)
  }}/>)
}

ContractConflicts = withPropsPresent(ContractConflicts, ['conflicts'])
ContractConflicts = withConflictsData(ContractConflicts, 'Loading conflicts...')
ContractConflicts = withVersionData(ContractConflicts, 'Loading version...')
ContractConflicts = withErrorHandling(ContractConflicts)
ContractConflicts = memo(ContractConflicts)

export {_ContractConflicts, ContractConflicts}


export default ContractConflicts